import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import api from "../lib/api";
import { extractError } from "../lib/formatError";

const ALL_PERIODS = ["24h", "7d", "30d", "permanent"];

export default function AdminRgPolicy() {
  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/rg-policy");
      setPolicy(data); setDraft(data);
    } catch (e) { setError(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!draft) return <div className="text-[color:var(--muted)] text-sm">Loading…</div>;

  const dirty = JSON.stringify(policy) !== JSON.stringify(draft);
  const save = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/admin/rg-policy", draft);
      setPolicy(data); setDraft(data);
      toast.success("Policy saved");
    } catch (e) { setError(extractError(e)); } finally { setBusy(false); }
  };

  const togglePeriod = (p) => {
    const cur = draft.allowed_self_exclusion_periods || [];
    if (cur.includes(p)) setDraft({ ...draft, allowed_self_exclusion_periods: cur.filter((x) => x !== p) });
    else setDraft({ ...draft, allowed_self_exclusion_periods: [...cur, p] });
  };

  return (
    <div data-testid="admin-rg-policy">
      <Toaster theme="dark" position="top-center" />
      <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)] mb-2">RG policy</div>
      <h1 className="font-display italic text-4xl text-[color:var(--ink)] mb-1">Global defaults</h1>
      <p className="text-sm text-[color:var(--muted)] mb-6 max-w-xl">
        Applied to <b>new signups only</b> — existing users keep their current limits (they can raise/lower via their RG settings).
      </p>

      {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Default deposit limits">
          <Num label="Daily ($)" value={draft.default_daily_limit_usd} onChange={(v) => setDraft({ ...draft, default_daily_limit_usd: Number(v) })} testid="rg-default-daily" />
          <Num label="Weekly ($)" value={draft.default_weekly_limit_usd} onChange={(v) => setDraft({ ...draft, default_weekly_limit_usd: Number(v) })} testid="rg-default-weekly" />
          <Num label="Monthly ($)" value={draft.default_monthly_limit_usd} onChange={(v) => setDraft({ ...draft, default_monthly_limit_usd: Number(v) })} testid="rg-default-monthly" />
        </Section>

        <Section title="Sessions & cooldown">
          <Num label="Session alert (min)" value={draft.default_session_alert_minutes} onChange={(v) => setDraft({ ...draft, default_session_alert_minutes: Number(v) })} step="1" testid="rg-default-alert" />
          <Num label="Limit-increase cooldown (h)" value={draft.cooldown_hours} onChange={(v) => setDraft({ ...draft, cooldown_hours: Number(v) })} step="1" min="24" testid="rg-cooldown" />
          <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] col-span-2">Cooldown may only be extended — regulator floor is 24h.</div>
        </Section>

        <Section title="Self-exclusion periods offered">
          <div className="col-span-2 flex flex-wrap gap-2">
            {ALL_PERIODS.map((p) => (
              <label key={p} className={`px-3 py-1.5 border cursor-pointer text-[10px] tracking-widest uppercase ${draft.allowed_self_exclusion_periods?.includes(p) ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--line)] text-[color:var(--muted)]"}`}>
                <input type="checkbox" checked={draft.allowed_self_exclusion_periods?.includes(p)} onChange={() => togglePeriod(p)} className="hidden" data-testid={`rg-period-${p}`} />
                {p}
              </label>
            ))}
          </div>
        </Section>
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={() => setDraft(policy)} disabled={!dirty || busy} className="px-4 py-2 border border-[color:var(--line)] text-[10px] tracking-widest uppercase text-[color:var(--muted)] disabled:opacity-40" data-testid="rg-reset">Reset</button>
        <button onClick={save} disabled={!dirty || busy} className="spin-btn px-8" data-testid="rg-save">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border p-4" style={{ borderColor: "var(--line)", background: "var(--card)" }}>
      <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}
function Num({ label, value, onChange, testid, ...rest }) {
  return (
    <label className="block col-span-2 sm:col-span-1">
      <div className="text-[9px] tracking-widest uppercase text-[color:var(--muted)] mb-1">{label}</div>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="bet-input text-xs font-mono" data-testid={testid} {...rest} />
    </label>
  );
}
